import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  createContext,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useContext,
  useEffect,
  useId,
  useRef,
} from "react";
import { Link } from "react-router";
import { ApiError } from "./api.ts";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

/** Every page names itself in the browser tab and to screen readers (WCAG 2.4.2). */
export function useDocumentTitle(title: ReactNode) {
  const text = typeof title === "string" ? title : null;
  useEffect(() => {
    if (!text) return;
    document.title = `${text} · recoup`;
    return () => {
      document.title = "recoup";
    };
  }, [text]);
}

/**
 * A row in the app's side navigation. The current row is a filled box with bolder ink, and
 * no edge mark: nothing drawn at the edge for a rounded corner to clip.
 */
export const navItemClass = ({ isActive }: { isActive: boolean }) =>
  cx(
    "block rounded-control px-3 py-2 text-sm font-medium",
    isActive
      ? "bg-surface-2 font-semibold text-ink"
      : "text-muted hover:bg-surface-2 hover:text-ink",
  );

/** Every page in the shell: one width, a title, one plain sentence, actions on the right. */
export function Page({
  title,
  lead,
  actions,
  back,
  help,
  children,
}: {
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
  /** The help center guide for this page (PR-24): its anchor on /help. */
  help?: string;
  children: ReactNode;
}) {
  useDocumentTitle(title);
  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      {back && (
        <Link
          to={back.to}
          className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
        >
          ← {back.label}
        </Link>
      )}
      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-bold tracking-tight text-balance">{title}</h1>
          {lead && <p className="mt-1 max-w-3xl text-muted">{lead}</p>}
          {help && (
            <Link to={`/help#${help}`} className="mt-1 inline-block text-sm text-accent">
              How this works
            </Link>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
      </header>
      <div className="flex flex-col gap-6">{children}</div>
    </div>
  );
}

/**
 * One Settings tab (TR-51): a stack of full-width sections with one gap, and nothing else, so
 * every tab's first card starts in the same place.
 */
export function SettingsTab({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6">{children}</div>;
}

/** A Settings card: an anchor, a title, one line on what it's for, and its actions. */
export function SettingsSection(props: {
  id: string;
  title: ReactNode;
  description: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  flush?: boolean;
}) {
  return <Card {...props} />;
}

export function Card({
  title,
  description,
  actions,
  children,
  flush,
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  flush?: boolean;
  id?: string;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-20 overflow-hidden rounded-card border border-line bg-surface shadow-card"
    >
      {(title || actions) && (
        <div className="flex flex-col gap-2 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {title && (
              <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
            )}
            {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      {children !== undefined && <div className={flush ? "" : "p-5"}>{children}</div>}
    </section>
  );
}

type Variant = "primary" | "secondary" | "ghost" | "danger";

/** One look for every button, whether it's a button, a link in the app or a download. */
export const buttonClass = (variant: Variant = "secondary", className?: string) =>
  cx(
    "inline-flex h-9 items-center justify-center gap-2 rounded-control px-3.5 text-sm font-medium whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-50",
    variant === "primary" && "bg-ink font-semibold text-bg hover:opacity-90",
    variant === "secondary" && "border border-line-strong bg-surface hover:bg-surface-2",
    variant === "ghost" && "text-muted hover:bg-surface-2 hover:text-ink",
    variant === "danger" && "border border-line-strong bg-surface text-danger hover:bg-danger-soft",
    className,
  );

export function Button({
  variant = "secondary",
  busy,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={rest.disabled || busy}
      className={buttonClass(variant, className)}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

export const ButtonLink = ({
  to,
  variant = "secondary",
  children,
}: {
  to: string;
  variant?: Variant;
  children: ReactNode;
}) => (
  <Link to={to} className={buttonClass(variant)}>
    {children}
  </Link>
);

/** A button-shaped link that leaves the app: a download, or a vendor's own form. */
export const ButtonAnchor = ({
  variant = "secondary",
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant }) => (
  <a {...rest} className={buttonClass(variant)}>
    {children}
  </a>
);

export const Spinner = () => (
  <span
    className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
    aria-hidden
  />
);

/** What a Field tells the control inside it: which hint or error describes it. */
const FieldContext = createContext<{ id: string; describedBy?: string; invalid: boolean } | null>(
  null,
);

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  const note = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <FieldContext.Provider value={{ id, describedBy: note, invalid: !!error }}>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {children(id)}
        {hint && !error && (
          <p id={note} className="text-xs text-muted">
            {hint}
          </p>
        )}
        {error && (
          <p id={note} role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

/** The hint or error a control's Field shows, read out with the control (WCAG 1.3.1, 3.3.1). */
function useFieldAria(id: string | undefined) {
  const f = useContext(FieldContext);
  if (!f || f.id !== id) return {};
  return { "aria-describedby": f.describedBy, "aria-invalid": f.invalid || undefined };
}

/** Full width unless the caller sets one: `w-full` would otherwise win over `w-32`. */
const control = (className?: string) =>
  cx(
    "rounded-control border border-line-strong bg-surface px-3 text-sm placeholder:text-faint focus:border-ink aria-invalid:border-danger",
    !/(^|\s)(\w+:)*w-/.test(className ?? "") && "w-full",
    className,
  );
export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => (
  <input {...useFieldAria(p.id)} {...p} className={control(cx("h-9", p.className))} />
);
export const Textarea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...useFieldAria(p.id)}
    {...p}
    className={control(cx("py-2 leading-relaxed", p.className))}
  />
);
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...useFieldAria(p.id)} {...p} className={control(cx("h-9", p.className))} />
);

type Tone = "neutral" | "ok" | "warn" | "danger" | "accent";
export const Badge = ({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) => (
  <span
    className={cx(
      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
      tone === "neutral" && "bg-surface-2 text-muted",
      tone === "ok" && "bg-ok-soft text-ok",
      tone === "warn" && "bg-warn-soft text-warn",
      tone === "danger" && "bg-danger-soft text-danger",
      tone === "accent" && "bg-accent-soft text-accent",
    )}
  >
    {children}
  </span>
);

export const Stat = ({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: "accent";
}) => (
  <div className="rounded-card border border-line bg-surface p-5 shadow-card">
    <div className="eyebrow">{label}</div>
    <div className="tabular mt-2 font-display text-3xl font-bold tracking-tight">
      {tone === "accent" ? <span className="marker">{value}</span> : value}
    </div>
    {note && <div className="mt-1 text-sm text-muted">{note}</div>}
  </div>
);

/** Arrow keys, Home and End move between a row of options, as screen-reader users expect. */
function arrowKeys<T>(e: KeyboardEvent, ids: T[], value: T, pick: (v: T) => void) {
  const i = ids.indexOf(value);
  const next =
    e.key === "ArrowRight"
      ? (i + 1) % ids.length
      : e.key === "ArrowLeft"
        ? (i - 1 + ids.length) % ids.length
        : e.key === "Home"
          ? 0
          : e.key === "End"
            ? ids.length - 1
            : -1;
  if (next < 0) return;
  e.preventDefault();
  pick(ids[next]!);
  const row = e.currentTarget as HTMLElement;
  row.querySelectorAll<HTMLElement>("[data-option]")[next]?.focus();
}

/**
 * Views of one record, one at a time (WAI-ARIA tabs). The shown view goes in `children`, so
 * it is the tab panel the selected tab names.
 */
export function Tabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
  children,
}: {
  /** What the views are of, for screen readers: "Claims", "Vendor". */
  label: string;
  tabs: { id: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  children: ReactNode;
}) {
  const base = useId();
  const ids = tabs.map((t) => t.id);
  return (
    <>
      <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
        <div
          role="tablist"
          aria-label={label}
          className="flex min-w-max gap-1 border-b border-line"
          onKeyDown={(e) => arrowKeys(e, ids, value, onChange)}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              id={`${base}-tab-${t.id}`}
              type="button"
              role="tab"
              data-option
              aria-selected={value === t.id}
              aria-controls={`${base}-panel`}
              tabIndex={value === t.id ? 0 : -1}
              onClick={() => onChange(t.id)}
              className={cx(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                value === t.id
                  ? "border-ink font-semibold text-ink"
                  : "border-transparent text-muted hover:text-ink",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div
        role="tabpanel"
        id={`${base}-panel`}
        aria-labelledby={`${base}-tab-${value}`}
        className="flex flex-col gap-6"
      >
        {children}
      </div>
    </>
  );
}

/** One choice that filters what's below it, such as a date range: a row of pressed buttons. */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="inline-flex gap-1 rounded-control border border-line bg-surface p-1">
      <legend className="sr-only">{label}</legend>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          className={cx(
            "h-7 rounded-chip px-3 text-sm font-medium whitespace-nowrap",
            value === o.id
              ? "bg-ink font-semibold text-bg"
              : "text-muted hover:bg-surface-2 hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </fieldset>
  );
}

export const Table = ({
  head,
  children,
  empty,
}: {
  head: ReactNode[];
  children: ReactNode;
  empty?: ReactNode;
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm">
      <thead className="eyebrow border-b border-line bg-surface-2 font-medium">
        <tr>
          {head.map((h, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static header cells
            <th key={i} className="px-5 py-2.5 whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-line">{children}</tbody>
    </table>
    {empty}
  </div>
);
export const Td = ({ children, className }: { children?: ReactNode; className?: string }) => (
  <td className={cx("px-5 py-3 align-top", className)}>{children}</td>
);

export const Empty = ({ title, children }: { title: string; children?: ReactNode }) => (
  <div className="px-5 py-10 text-center">
    <p className="font-medium">{title}</p>
    {children && <div className="mt-1 text-sm text-muted">{children}</div>}
  </div>
);

export const Notice = ({
  tone = "warn",
  children,
}: {
  tone?: "warn" | "danger" | "ok" | "accent";
  children: ReactNode;
}) => (
  <div
    role={tone === "danger" ? "alert" : undefined}
    className={cx(
      "rounded-control px-4 py-3 text-sm",
      tone === "warn" && "bg-warn-soft text-warn",
      tone === "danger" && "bg-danger-soft text-danger",
      tone === "ok" && "bg-ok-soft text-ok",
      tone === "accent" && "bg-accent-soft text-accent",
    )}
  >
    {children}
  </div>
);

export const ErrorText = ({ error }: { error: unknown }) =>
  error ? (
    <Notice tone="danger">
      {error instanceof ApiError || error instanceof Error
        ? error.message
        : "Something went wrong."}
    </Notice>
  ) : null;

/**
 * What a page shows when its record couldn't load: plain words and the way back, so a stale
 * or mistyped link is never a dead end.
 */
export function LoadError({
  what,
  error,
  back,
}: {
  what: string;
  error: unknown;
  back: { to: string; label: string };
}) {
  const gone = error instanceof ApiError && (error.status === 400 || error.status === 404);
  if (!gone) return <ErrorText error={error ?? new Error("Something went wrong.")} />;
  return (
    <Card>
      <Empty title={`This ${what} doesn't exist, or it was removed.`}>
        <Link className="text-accent" to={back.to}>
          Back to {back.label}
        </Link>
      </Empty>
    </Card>
  );
}

export const Loading = () => (
  <div className="flex items-center gap-2 px-4 py-10 text-sm text-muted md:px-8">
    <Spinner /> Loading…
  </div>
);

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby={titleId}
      className="m-auto max-h-[calc(100dvh-32px)] w-[min(560px,calc(100vw-32px))] flex-col rounded-card border border-line bg-surface p-0 text-ink shadow-pop backdrop:bg-black/40 open:flex"
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-5 py-4">
        <h2 id={titleId} className="font-display text-lg font-semibold tracking-tight">
          {title}
        </h2>
        {/* Always there, however long the content: Escape isn't on every keyboard. */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="-mr-1 rounded-control px-2 text-xl leading-none text-muted hover:bg-surface-2 hover:text-ink"
        >
          ×
        </button>
      </div>
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-5">{children}</div>
      {footer && (
        <div className="flex shrink-0 justify-end gap-2 border-t border-line px-5 py-3">
          {footer}
        </div>
      )}
    </dialog>
  );
}

/** A month strip: one cell per month, met / missed / provisional. */
export const Strip = ({
  months,
}: {
  months: { key: string; met: boolean; provisional?: boolean; creditCents?: string | null }[];
}) => (
  <div
    className="flex gap-0.5"
    role="img"
    aria-label={`${months.filter((m) => !m.provisional && !m.met).length} of ${months.filter((m) => !m.provisional).length} months missed`}
  >
    {months.map((m) => (
      <span
        key={m.key}
        title={`${m.key}: ${m.provisional ? "in progress" : m.met ? "met" : "missed"}`}
        className={cx(
          "h-5 w-2.5 rounded-sm",
          m.provisional ? "bg-line" : m.met ? "bg-ok/60" : "bg-danger",
        )}
      />
    ))}
  </div>
);

export { cx };
